import React, { useMemo, useState } from "react";
import { Row, Col } from "react-bootstrap";
import { useSelector } from "react-redux";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faNewspaper, faXmark } from "@fortawesome/free-solid-svg-icons";
import styled from "styled-components";
import ReservationItems from "./Reservations/ReservationItems";
import { DefaultDateSmallMins } from "./DefaultImports";

// Colors
const COLOR_ORANGE = "#EE754C";
const COLOR_GRAY = "#99B0AD";

const Modal = styled.div`
  position: fixed;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 90%;
  max-width: 520px;
  height: 70%;
  background: #fff;
  border-radius: 7px;
  z-index: 999;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.15);
`;

const ModalHeader = styled.div`
  background: ${COLOR_GRAY};
  color: #faf6e9;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 16px;
  font-size: 18px;
  font-weight: bold;
  width: 100%;
`;

const ModalBody = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 12px 16px;
`;

const CloseButton = styled.div`
  cursor: pointer;
  color: #faf6e9;
  font-size: 20px;
`;

const HistoryBox = styled.div`
  background: white;
  border: 1px solid ${COLOR_GRAY};
  border-radius: 8px;
  margin-bottom: 12px;
  padding: 10px;
`;

const HistoryHeader = styled.div`
  background: ${COLOR_GRAY};
  color: #faf6e9;
  padding: 6px 10px;
  font-size: 14px;
  font-weight: bold;
  border-radius: 6px 6px 0 0;
`;

const EmptyState = styled.div`
  color: ${COLOR_GRAY};
  font-size: 14px;
  text-align: center;
  padding: 28px 10px;
`;

const normalizeValue = (value) => {
  if (value === undefined || value === null) return "";
  return String(value).trim();
};

const normalizeDate = (value) => {
  if (!value) return "";
  return String(value).slice(0, 10);
};

const normalizeTime = (value) => {
  if (!value) return "";
  return String(value).slice(0, 5);
};

const normalizeNumber = (value) => {
  if (value === undefined || value === null || value === "") return "";
  const number = Number(value);
  return Number.isNaN(number) ? String(value).trim() : String(number);
};

const getIdValue = (value) => {
  if (value && typeof value === "object") {
    return value.id ?? value.pk ?? "";
  }

  return value ?? "";
};

const isNonZero = (value) => {
  const number = Number(value);
  return !Number.isNaN(number) && number !== 0;
};

const isCancelledStatus = (status) => {
  return [
    "Cancelled",
    "cancelled",
    "cancelled_customer",
    "cancelled_restaurant",
  ].includes(status);
};

const formatPhoneNumber = (phone, countryCode) => {
  if (!phone) return "";

  if (!countryCode) return String(phone).trim();

  const cleanPhone = String(phone).replace(/\s/g, "");
  const cleanCode = String(countryCode).replace(/\D/g, "");

  if (!cleanCode) return cleanPhone;

  if (cleanPhone.startsWith(cleanCode)) {
    return `+${cleanCode} ${cleanPhone.slice(cleanCode.length)}`.trim();
  }

  return `+${cleanCode} ${cleanPhone}`.trim();
};

const calcConfigSum = (numOfTables, configList = []) => {
  if (!Array.isArray(configList) || numOfTables < 2) return 0;

  const chairsByTableCount = configList.reduce((acc, item) => {
    const tableCount = Number(item?.number_of_tables);
    const chairs = Number(item?.number_of_chairs) || 0;

    if (!Number.isNaN(tableCount)) {
      acc[tableCount] = chairs;
    }

    return acc;
  }, {});

  let configSum = 0;

  if (numOfTables >= 2) configSum += chairsByTableCount[2] || 0;
  if (numOfTables >= 3) configSum += chairsByTableCount[3] || 0;
  if (numOfTables >= 4) configSum += chairsByTableCount[4] || 0;

  for (let i = 5; i <= numOfTables; i++) {
    configSum += chairsByTableCount[((i + 1) % 2) + 1] || 0;
  }

  return configSum;
};

const parseTableIdsFromString = (tablesString) => {
  if (!tablesString) return [];

  return String(tablesString)
    .split(",")
    .map((n) => parseInt(n, 10))
    .filter((n) => !Number.isNaN(n))
    .sort((a, b) => a - b);
};

const getTablesSummaryLabel = (tablesString, allTables, configList) => {
  if (!tablesString || !Array.isArray(allTables)) return "";

  const tableIds = parseTableIdsFromString(tablesString);

  if (tableIds.length === 0) return "";

  const filteredTables = allTables.filter((table) =>
    tableIds.includes(table.id),
  );

  const baseSeats = filteredTables.reduce(
    (acc, table) => acc + (Number.parseInt(table.number_of_seats, 10) || 0),
    0,
  );

  const tableCount = filteredTables.length;
  const configSum = calcConfigSum(tableCount, configList);
  const totalChairs = baseSeats + configSum;

  return `${tableCount} Mesas / ${totalChairs} Cadeiras`;
};

const getZoneLabel = (zoneId, zonesWithId) => {
  const id = getIdValue(zoneId);

  if (id === "") return "Sala não definida";

  return zonesWithId?.[id] ?? String(id);
};

const valuesChanged = (previousValue, currentValue) => {
  return normalizeValue(previousValue) !== normalizeValue(currentValue);
};

const hasVisibleReservationChange = ({
  reservation,
  previousReservation,
  allTables,
  tablesConfigList,
  zonesWithId,
}) => {
  if (!reservation || !previousReservation) return false;

  const checks = [];

  // Nome
  checks.push([previousReservation.full_name, reservation.full_name]);

  // Telefone confirmado
  checks.push([
    previousReservation.is_phone_confirmed,
    reservation.is_phone_confirmed,
  ]);

  // Telemóvel visível com código do país
  checks.push([
    formatPhoneNumber(
      previousReservation.phone_number,
      previousReservation.country_code,
    ),
    formatPhoneNumber(reservation.phone_number, reservation.country_code),
  ]);

  // Data
  checks.push([
    normalizeDate(previousReservation.date),
    normalizeDate(reservation.date),
  ]);

  // Hora
  checks.push([
    normalizeTime(previousReservation.time),
    normalizeTime(reservation.time),
  ]);

  // Nº de pessoas
  checks.push([
    normalizeNumber(previousReservation.number_of_people),
    normalizeNumber(reservation.number_of_people),
  ]);

  // Cadeira de bebé
  if (
    isNonZero(previousReservation.baby_chair) ||
    isNonZero(reservation.baby_chair)
  ) {
    checks.push([
      normalizeNumber(previousReservation.baby_chair),
      normalizeNumber(reservation.baby_chair),
    ]);
  }

  // Zona
  checks.push([
    getZoneLabel(previousReservation.table_place_preference, zonesWithId),
    getZoneLabel(reservation.table_place_preference, zonesWithId),
  ]);

  // Mesas usadas
  const shouldCompareTables =
    Array.isArray(allTables) &&
    !isCancelledStatus(reservation.status) &&
    (previousReservation.tablesused || reservation.tablesused);

  if (shouldCompareTables) {
    checks.push([
      getTablesSummaryLabel(
        previousReservation.tablesused,
        allTables,
        tablesConfigList,
      ),
      getTablesSummaryLabel(
        reservation.tablesused,
        allTables,
        tablesConfigList,
      ),
    ]);
  }

  // Mesas insuficientes
  checks.push([
    previousReservation.not_enough_seats,
    reservation.not_enough_seats,
  ]);

  // Estado
  checks.push([previousReservation.status, reservation.status]);

  // Acessibilidade
  if (
    previousReservation.accessability === "wheel_chair" ||
    reservation.accessability === "wheel_chair"
  ) {
    checks.push([
      previousReservation.accessability === "wheel_chair"
        ? "Mob. Red."
        : "Nenhuma",
      reservation.accessability === "wheel_chair" ? "Mob. Red." : "Nenhuma",
    ]);
  }

  return checks.some(([previousValue, currentValue]) =>
    valuesChanged(previousValue, currentValue),
  );
};

const getChangeAuthorLabel = (changesMadeBy) => {
  const value = changesMadeBy?.toLowerCase();

  if (value === "system") return "Restaurante";
  if (value === "customer") return "Cliente";
  if (value === "restaurant") return "Restaurante";

  return changesMadeBy || "Sistema";
};

const ReservationHistory = ({
  reservationHistory,
  timezone,
  all_tables,
  zones_with_id,
}) => {
  const [showModal, setShowModal] = useState(false);

  const tablesConfigList = useSelector(
    (state) => state.connectTablesConfig?.connectTablesConfig || [],
  );

  const visibleHistory = useMemo(() => {
    if (!Array.isArray(reservationHistory)) return [];

    return reservationHistory
      .map((reservation, index) => {
        const previousReservation = reservationHistory[index + 1] || null;

        return {
          reservation,
          previousReservation,
        };
      })
      .filter(({ reservation, previousReservation }) =>
        hasVisibleReservationChange({
          reservation,
          previousReservation,
          allTables: all_tables,
          tablesConfigList,
          zonesWithId: zones_with_id,
        }),
      );
  }, [reservationHistory, all_tables, tablesConfigList, zones_with_id]);

  return (
    <>
      <FontAwesomeIcon
        icon={faNewspaper}
        style={{
          color: "#FAF6E9",
          fontSize: "26px",
          cursor: "pointer",
        }}
        onClick={() => setShowModal(true)}
      />

      {showModal && (
        <Modal>
          <ModalHeader>
            Registo de atividade
            <CloseButton onClick={() => setShowModal(false)}>
              <FontAwesomeIcon icon={faXmark} />
            </CloseButton>
          </ModalHeader>

          <ModalBody>
            {visibleHistory.length === 0 ? (
              <EmptyState>Sem alterações relevantes para mostrar.</EmptyState>
            ) : (
              visibleHistory.map(
                ({ reservation, previousReservation }, index) => (
                  <HistoryBox
                    key={
                      reservation.history_id ||
                      `${reservation.id}-${reservation.history_date}-${index}`
                    }
                  >
                    <HistoryHeader>
                      <Row>
                        <Col xs={4}>
                          Atualização #{visibleHistory.length - index}
                        </Col>
                        <Col xs={4} style={{ textTransform: "capitalize" }}>
                          {getChangeAuthorLabel(reservation.changes_made_by)}
                        </Col>
                        <Col xs={4}>
                          <DefaultDateSmallMins
                            date={reservation.history_date}
                          />
                        </Col>
                      </Row>
                    </HistoryHeader>

                    <div style={{ padding: "8px 4px" }}>
                      <ReservationItems
                        previousReservationFromHistory={previousReservation}
                        reservation={reservation}
                        all_tables={all_tables}
                        isReservationHistory={true}
                      />
                    </div>
                  </HistoryBox>
                ),
              )
            )}
          </ModalBody>
        </Modal>
      )}
    </>
  );
};

export default ReservationHistory;
